import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { Container, FlaskConical, LayoutDashboard, Sparkles, User } from "lucide-react";

import { orcaPortalUrl } from "~/lib/orca-portal-url.ts";
import { requireAdmin } from "~/lib/server-admin.ts";

/**
 * Admin layout with sidebar navigation
 *
 * Authorization is enforced server-side by `requireAdmin` in `beforeLoad`: an
 * unauthenticated visitor is sent to the brand login and a signed-in non-admin
 * is bounced to `/access-denied`, so by the time this component renders the
 * caller is guaranteed to be an admin. The public `/admin/login` route lives
 * outside `_authenticated`, so it is never caught by this guard.
 *
 * @since 1.0.0
 */
export const Route = createFileRoute("/flirtmachine/_authenticated/admin")({
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack redirects require async/await
  beforeLoad: async () => {
    // oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack redirects require async/await
    const auth = await requireAdmin();
    return { auth };
  },
  component: AdminLayout,
});

/**
 * Admin layout with sidebar navigation. Renders child routes via Outlet.
 *
 * @since 1.0.0
 */
function AdminLayout() {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-300 flex font-mono text-sm">
      {/* Sidebar */}
      <aside className="w-64 border-r border-zinc-800 bg-zinc-900 fixed h-full">
        <div className="p-4 border-b border-zinc-800">
          <div className="text-lg font-bold tracking-widest uppercase text-zinc-200">
            FlirtMachine
          </div>
          <div className="text-[10px] text-zinc-600 uppercase tracking-widest mt-1">
            Admin Console
          </div>
        </div>
        <nav className="py-4 px-2 space-y-1">
          <Link
            to="/flirtmachine/admin"
            activeOptions={{ exact: true }}
            className="flex items-center gap-3 px-3 py-2 text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-200 transition-colors [&.active]:bg-zinc-800 [&.active]:text-zinc-200 [&.active]:border-l-2 [&.active]:border-primary"
          >
            <LayoutDashboard className="w-4 h-4" />
            <span className="font-medium">Dashboard</span>
          </Link>
          <Link
            to="/flirtmachine/admin/quests"
            className="flex items-center gap-3 px-3 py-2 text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-200 transition-colors [&.active]:bg-zinc-800 [&.active]:text-zinc-200 [&.active]:border-l-2 [&.active]:border-primary"
          >
            <Sparkles className="w-4 h-4" />
            <span className="font-medium">Quests</span>
          </Link>
          <Link
            to="/flirtmachine/admin/personas"
            className="flex items-center gap-3 px-3 py-2 text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-200 transition-colors [&.active]:bg-zinc-800 [&.active]:text-zinc-200 [&.active]:border-l-2 [&.active]:border-primary"
          >
            <User className="w-4 h-4" />
            <span className="font-medium">Personas</span>
          </Link>
          <a
            href={orcaPortalUrl(typeof window === "undefined" ? "" : window.location.hostname)}
            className="flex items-center gap-3 px-3 py-2 text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-200 transition-colors [&.active]:bg-zinc-800 [&.active]:text-zinc-200 [&.active]:border-l-2 [&.active]:border-primary"
          >
            <Container className="w-4 h-4" />
            <span className="font-medium">Orca</span>
          </a>
          <Link
            to="/flirtmachine/admin/simulator"
            className="flex items-center gap-3 px-3 py-2 text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-200 transition-colors [&.active]:bg-zinc-800 [&.active]:text-zinc-200 [&.active]:border-l-2 [&.active]:border-primary"
          >
            <FlaskConical className="w-4 h-4" />
            <span className="font-medium">Simulator</span>
          </Link>
        </nav>
        <div className="absolute bottom-4 left-4 right-4">
          <Link
            to="/flirtmachine/dashboard"
            className="text-xs text-zinc-500 hover:text-zinc-300"
          >
            ← Back to App
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 flex flex-col min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}
